"use client";

import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { Check, FolderOpen, RefreshCw, Save, Search } from "lucide-react";
import Link from "next/link";
import { useLocale, useTranslations } from "next-intl";
import { useMemo, useState } from "react";
import { getLanguageById } from "../config/languages";
import { useMySnippets } from "../hooks";
import { cn } from "@/lib/utils";

const triggerClass =
  "inline-flex h-11 min-h-[44px] min-w-[44px] shrink-0 items-center justify-center gap-2 rounded-lg border border-white/15 bg-white/5 px-0 text-sm font-medium text-white/90 transition hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#E94D1D]/45 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0B0B0C] md:h-10 md:min-h-0 xl:min-w-0 xl:px-3";

type PlaygroundSnippetsQuickAccessProps = {
  locale: string;
  activeSnippetId: string | null;
  onSelectSnippet: (snippetId: string, languageId: string) => void;
  onSaveClick: () => void;
};

function formatUpdatedAt(value: string | undefined, locale: string): string | null {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat(locale, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
}

export function PlaygroundSnippetsQuickAccess({
  locale,
  activeSnippetId,
  onSelectSnippet,
  onSaveClick,
}: PlaygroundSnippetsQuickAccessProps) {
  const t = useTranslations("playground");
  const currentLocale = useLocale();
  const [search, setSearch] = useState("");
  const { data, isLoading, isError, isFetching, refetch } = useMySnippets(
    { page: 1, limit: 20 },
    true,
  );

  const snippets = data?.snippets ?? [];
  const filteredSnippets = useMemo(() => {
    const query = search.trim().toLocaleLowerCase(currentLocale);
    if (!query) return snippets;
    return snippets.filter((snippet) => {
      const language = getLanguageById(snippet.languageId);
      return (
        snippet.title.toLocaleLowerCase(currentLocale).includes(query) ||
        language?.name.toLocaleLowerCase(currentLocale).includes(query)
      );
    });
  }, [currentLocale, search, snippets]);

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          title={t("accountMenuMySnippets")}
          aria-label={t("accountMenuMySnippets")}
          className={triggerClass}
        >
          <FolderOpen className="h-4 w-4 shrink-0 text-[#E94D1D]" aria-hidden />
          <span className="hidden xl:inline">{t("accountMenuMySnippets")}</span>
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          className="z-[200] w-[min(100vw-2rem,380px)] rounded-xl border border-white/10 bg-[#141416] p-1.5 shadow-2xl"
          sideOffset={6}
          align="start"
          collisionPadding={8}
        >
          <div className="px-1.5 pb-2 pt-1">
            <div className="mb-2 flex items-center justify-between gap-3">
              <div>
                <p className="text-sm font-semibold text-white">{t("accountMenuMySnippets")}</p>
                <p className="text-xs text-white/50">{t("quickSnippetsRecent")}</p>
              </div>
              {isFetching && !isLoading ? (
                <span className="h-2 w-2 animate-pulse rounded-full bg-[#E94D1D]" aria-hidden />
              ) : null}
            </div>
            <label className="relative block">
              <span className="sr-only">{t("quickSnippetsSearchPlaceholder")}</span>
              <Search
                className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-white/40"
                aria-hidden
              />
              <input
                type="search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                onKeyDown={(event) => event.stopPropagation()}
                placeholder={t("quickSnippetsSearchPlaceholder")}
                className="h-11 w-full rounded-lg border border-white/15 bg-[#0B0B0C] pl-9 pr-3 text-sm text-white outline-none placeholder:text-white/35 focus:border-[#E94D1D]/70 focus:ring-2 focus:ring-[#E94D1D]/30"
              />
            </label>
          </div>

          {isLoading ? (
            <div className="space-y-1 px-1 py-1" aria-label={t("loading")}>
              {[0, 1, 2].map((item) => (
                <div
                  key={item}
                  className="flex min-h-14 animate-pulse items-center gap-3 rounded-lg px-2.5 py-2"
                >
                  <span className="h-8 w-8 shrink-0 rounded-lg bg-white/10" />
                  <span className="min-w-0 flex-1 space-y-2">
                    <span className="block h-3 w-3/5 rounded bg-white/10" />
                    <span className="block h-2.5 w-2/5 rounded bg-white/5" />
                  </span>
                </div>
              ))}
            </div>
          ) : isError ? (
            <div className="px-3 py-5 text-center">
              <p className="text-sm text-white/60">{t("mySnippetsLoadFailed")}</p>
              <button
                type="button"
                onClick={() => void refetch()}
                className="mt-3 inline-flex min-h-11 items-center gap-2 rounded-lg border border-white/15 px-3 text-sm font-medium text-white transition hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#E94D1D]/45"
              >
                <RefreshCw className="h-4 w-4 text-[#E94D1D]" aria-hidden />
                {t("quickSnippetsRetry")}
              </button>
            </div>
          ) : snippets.length === 0 ? (
            <div className="px-3 py-5 text-center">
              <p className="text-sm font-medium text-white">{t("quickSnippetsEmpty")}</p>
              <p className="mt-1 text-xs leading-relaxed text-white/50">
                {t("quickSnippetsEmptyDescription")}
              </p>
              <DropdownMenu.Item asChild>
                <button
                  type="button"
                  onClick={onSaveClick}
                  className="mx-auto mt-3 flex min-h-11 cursor-pointer select-none items-center gap-2 rounded-lg bg-[#E94D1D] px-3 text-sm font-semibold text-white outline-none transition hover:bg-[#d44315] focus-visible:ring-2 focus-visible:ring-[#FFAB8F]"
                >
                  <Save className="h-4 w-4" aria-hidden />
                  {t("quickSnippetsSaveCurrent")}
                </button>
              </DropdownMenu.Item>
            </div>
          ) : filteredSnippets.length === 0 ? (
            <p className="px-3 py-6 text-center text-sm text-white/55">
              {t("quickSnippetsNoResults")}
            </p>
          ) : (
            <div
              className="max-h-[min(55vh,360px)] overflow-y-auto py-0.5"
              aria-label={t("quickSnippetsRecent")}
            >
              {filteredSnippets.map((snippet) => {
                const language = getLanguageById(snippet.languageId);
                const updatedAt = formatUpdatedAt(snippet.updatedAt, currentLocale);
                const isActive = snippet._id === activeSnippetId;
                return (
                  <DropdownMenu.Item
                    key={snippet._id}
                    className={cn(
                      "flex min-h-14 cursor-pointer select-none items-center gap-2.5 rounded-lg border border-transparent px-2.5 py-2 text-sm text-white/90 outline-none data-[highlighted]:bg-white/10",
                      isActive && "border-[#E94D1D]/35 bg-[#E94D1D]/10",
                    )}
                    onSelect={() => onSelectSnippet(snippet._id, snippet.languageId)}
                  >
                    <span
                      className={cn(
                        "flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-white/10 text-white/55",
                        isActive && "bg-[#E94D1D]/20 text-[#FFAB8F]",
                      )}
                    >
                      {isActive ? (
                        <Check className="h-4 w-4" aria-hidden />
                      ) : (
                        <span className="text-[10px] font-semibold uppercase">
                          {language?.fileExtension.replace(".", "") ?? "code"}
                        </span>
                      )}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="flex items-center gap-2">
                        <span className="truncate font-medium">{snippet.title}</span>
                        {isActive ? (
                          <span className="shrink-0 rounded-full bg-[#E94D1D]/20 px-1.5 py-0.5 text-[10px] font-semibold text-[#FFAB8F]">
                            {t("quickSnippetsActive")}
                          </span>
                        ) : null}
                      </span>
                      <span className="mt-0.5 flex min-w-0 items-center gap-1.5 text-xs text-white/45">
                        <span className="truncate">{language?.name ?? snippet.languageId}</span>
                        {updatedAt ? (
                          <>
                            <span aria-hidden>·</span>
                            <span className="truncate">{updatedAt}</span>
                          </>
                        ) : null}
                      </span>
                    </span>
                  </DropdownMenu.Item>
                );
              })}
            </div>
          )}

          <div className="border-t border-white/10 px-1 py-1">
            <DropdownMenu.Item asChild>
              <Link
                href={`/${locale}/dashboard/playground`}
                className="flex min-h-11 cursor-pointer select-none items-center justify-center rounded-lg px-2.5 text-sm font-medium text-[#FFAB8F] outline-none data-[highlighted]:bg-white/10"
              >
                {t("quickSnippetsManageAll")}
              </Link>
            </DropdownMenu.Item>
          </div>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
